// ===============================
// BOOT WATCHDOG — señal "bundle"
// El index.html arma un watchdog para auto-recuperar mobile con SW roto.
// Si esta marca no aparece en X seg, asume bundle muerto y nukea caches.
// ===============================
try { window.__IMPULZA_BOOT__ = 'bundle'; } catch (e) { /* best-effort: no bloquear arranque */ }

// ===============================
// SENTRY — inicializar PRIMERO, antes de todo
// Así captura errores desde el inicio del ciclo de vida
// ===============================
import './utils/sentry';
import * as Sentry from '@sentry/react';

import React from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from "react-router-dom";
import { Toaster } from 'react-hot-toast';

// ===============================
// POLYFILL crypto.randomUUID
// Necesario para Safari / iOS / HTTP por IP
// Evita que se rompa el click del catalogo en POS
// ===============================
if (typeof crypto !== "undefined" && !crypto.randomUUID) {
  crypto.randomUUID = function randomUUID() {
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === "x" ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  };
}

// ===============================
// ESTILOS
// ===============================
import 'bootstrap/dist/css/bootstrap.min.css';
// bootstrap-icons removido: eran 100 KB de CSS (~2.078 clases declaradas) en el
// camino critico del arranque para 6 iconos, todos en VentasNueva.jsx. Se
// reemplazaron por lucide-react, que ya viaja en icons-vendor.
import './index.css';

// ===============================
// SERVICE WORKER — AUTO RELOAD
// Cuando hay una nueva versión deployada, el SW se actualiza
// y esta función recarga la página automáticamente.
// Los usuarios siempre verán la última versión sin Ctrl+Shift+R.
// ===============================
import { registerSWAutoReload } from './utils/swUpdate';
registerSWAutoReload();

// ===============================
// GUARD: rueda del mouse sobre inputs numericos
// Evita que al scrollear una grilla se alteren importes ya cargados.
// ===============================
import { installNumberInputScrollGuard } from './utils/numberInputScrollGuard';
installNumberInputScrollGuard();

// ===============================
// LISTENER GLOBAL: chunk JS roto post-deploy
// Cuando el browser falla al cargar un modulo dinamico (MIME type / ChunkLoadError)
// porque el index.html cacheado referencia hashes viejos, nukeamos caches+SW y
// recargamos. El wrapper de lazy ya lo hace para imports controlados, pero esto
// cubre cualquier import dinamico (Sentry, Workbox window, etc.) que escape de el.
// ===============================
import { looksLikeChunkLoadError } from './utils/lazyWithRetry';

(function armChunkErrorWatcher() {
  const FLAG = 'impulza_chunk_reload_global_v1';
  // Mismo cooldown que lazyWithRetry: sin esto el flag se marcaba una vez y la
  // pestaña no volvia a intentar recuperarse nunca mas. Ver el comentario
  // extendido en utils/lazyWithRetry.js.
  const RETRY_COOLDOWN_MS = 10 * 60 * 1000;
  function alreadyTried() {
    try {
      const raw = window.sessionStorage && window.sessionStorage.getItem(FLAG);
      if (!raw) return false;
      const ultimoIntento = Number(raw);
      if (!Number.isFinite(ultimoIntento)) return true;
      return (Date.now() - ultimoIntento) < RETRY_COOLDOWN_MS;
    }
    catch (e) { return false; }
  }
  function markTried() {
    try { window.sessionStorage && window.sessionStorage.setItem(FLAG, String(Date.now())); }
    catch (e) { /* best-effort: no bloquear arranque */ }
  }
  async function nukeAndReload() {
    if (alreadyTried()) return;
    markTried();
    try {
      if ('serviceWorker' in navigator && navigator.serviceWorker.getRegistrations) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister().catch(() => {})));
      }
    } catch (e) { /* best-effort: no bloquear arranque */ }
    try {
      if (window.caches && caches.keys) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k).catch(() => {})));
      }
    } catch (e) { /* best-effort: no bloquear arranque */ }
    try { window.location.reload(); } catch (e) { /* best-effort: no bloquear arranque */ }
  }
  window.addEventListener('error', (event) => {
    if (looksLikeChunkLoadError(event?.error || event?.message)) nukeAndReload();
  });
  window.addEventListener('unhandledrejection', (event) => {
    if (looksLikeChunkLoadError(event?.reason)) nukeAndReload();
  });
})();

// ===============================
// APP
// ===============================
import { AuthProvider } from './context/AuthContext';
import { SiteProvider } from './context/SiteContext';
import App from './App';

// ===============================
// SENTRY ERROR BOUNDARY — fallback visual cuando crashea la app
// ===============================
async function nukeCachesAndReload() {
  try {
    if ('serviceWorker' in navigator && navigator.serviceWorker.getRegistrations) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister().catch(() => {})));
    }
  } catch (e) { /* best-effort: no bloquear arranque */ }
  try {
    if (window.caches && caches.keys) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k).catch(() => {})));
    }
  } catch (e) { /* best-effort: no bloquear arranque */ }
  try { window.location.reload(); } catch (e) { /* best-effort: no bloquear arranque */ }
}

function SentryFallback({ error, resetError }) {
  const isChunkError = looksLikeChunkLoadError(error);
  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      padding: '2rem',
      background: '#f8fafc',
      textAlign: 'center',
    }}>
      <div style={{
        background: '#fff',
        borderRadius: '16px',
        padding: '2.5rem',
        maxWidth: '480px',
        boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
      }}>
        <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>⚠️</div>
        <h2 style={{ color: '#0f172a', marginBottom: '0.5rem' }}>
          {isChunkError ? 'Hay una versión nueva disponible' : 'Algo salió mal'}
        </h2>
        <p style={{ color: '#64748b', marginBottom: '1.5rem' }}>
          {isChunkError
            ? 'Necesitamos recargar la app para traer los archivos actualizados.'
            : 'El error fue reportado automáticamente. Podés intentar recargar la página.'}
        </p>
        {error && (
          <details style={{ textAlign: 'left', marginBottom: '1.5rem', fontSize: '0.8rem', color: '#94a3b8' }}>
            <summary style={{ cursor: 'pointer' }}>Ver detalle del error</summary>
            <pre style={{ marginTop: '0.5rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {error.toString()}
            </pre>
          </details>
        )}
        <button
          onClick={isChunkError ? nukeCachesAndReload : resetError}
          style={{
            background: '#0B2A36',
            color: '#fff',
            border: 'none',
            borderRadius: '8px',
            padding: '0.75rem 2rem',
            cursor: 'pointer',
            fontWeight: 700,
            fontSize: '1rem',
            marginRight: '0.75rem',
          }}
        >
          {isChunkError ? 'Recargar app' : 'Reintentar'}
        </button>
        <button
          onClick={() => window.location.href = '/'}
          style={{
            background: 'transparent',
            color: '#0B2A36',
            border: '2px solid #0B2A36',
            borderRadius: '8px',
            padding: '0.75rem 2rem',
            cursor: 'pointer',
            fontWeight: 700,
            fontSize: '1rem',
          }}
        >
          Ir al inicio
        </button>
      </div>
    </div>
  );
}

// ===============================
// RENDER
// ===============================
ReactDOM.createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <Sentry.ErrorBoundary fallback={SentryFallback} showDialog={false}>
      <AuthProvider>
        <SiteProvider>
          <BrowserRouter>
            <App />
            <Toaster
              position="top-right"
              toastOptions={{
                duration: 3500,
                style: {
                  borderRadius: '16px',
                  background: '#0f172a',
                  color: '#f8fafc',
                  fontWeight: 700,
                },
              }}
            />
          </BrowserRouter>
        </SiteProvider>
      </AuthProvider>
    </Sentry.ErrorBoundary>
  </React.StrictMode>
);

try { window.__IMPULZA_BOOT__ = 'rendered'; } catch (e) { /* best-effort: no bloquear arranque */ }
